import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { LoadingGuard } from "../../../components/LoadingGuard";
import { EnigmaAnswer } from "../../../libs/gameAnalysis/enigma";
import { WorkerQueryTransferModes } from "../../../libs/workers/consts";
import { webWorkerQuery } from "../../../libs/workers/webWorker";
import EnigmaUserAnswersWorker from "./EnigmaUserAnswers.worker.ts?worker";

export type EnigmaUserAnswersProps = {
  cityEscapeId?: string | null;
  enigmaId?: string;
  sx?: {
    minWidth?: number;
  };
};

export function EnigmaUserAnswers({
  cityEscapeId,
  enigmaId,
  sx: { minWidth = 300 } = { minWidth: 300 },
}: EnigmaUserAnswersProps) {
  const userAnswers = useEnigmaUserAnswers(cityEscapeId, enigmaId);
  const theme = useTheme();

  if (userAnswers.data) {
    console.log("userAnswers.data", userAnswers.data);
  }

  return (
    <Card sx={{ overflowY: "auto", position: "relative", minWidth: minWidth }}>
      <CardHeader
        title={`Risposte fornite (${
          userAnswers.data?.reduce((acc, a) => acc + a.occurrences, 0) || 0
        })`}
        slotProps={{
          root: {
            style: {
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
            },
          },
        }}
      >
        <Typography variant="h6" gutterBottom></Typography>
      </CardHeader>
      <CardContent>
        <LoadingGuard isLoading={userAnswers.isLoading}>
          <Stack direction="column" width={"100%"} maxHeight={0}>
            <List>
              {userAnswers.data?.map((answer, i) => {
                const chipColor = answer.accepted
                  ? "success"
                  : answer.managed
                  ? "warning"
                  : "error";

                return (
                  <ListItem key={i} disableGutters disablePadding>
                    <ListItemIcon>
                      <Chip
                        size="small"
                        label={answer.occurrences}
                        color={chipColor}
                      />
                    </ListItemIcon>
                    <ListItemText primary={answer.text} />
                  </ListItem>
                );
              })}
            </List>
          </Stack>
        </LoadingGuard>
      </CardContent>
    </Card>
  );
}

function useEnigmaUserAnswers(cityEscapeId?: string | null, enigmaId?: string) {
  return useQuery({
    queryKey: ["EnigmaUserAnswers", cityEscapeId, enigmaId],
    queryFn: () => getUserAnswers(cityEscapeId, enigmaId),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

function getUserAnswers(cityEscapeId?: string | null, enigmaId?: string) {
  const worker = new EnigmaUserAnswersWorker();
  return webWorkerQuery<string, EnigmaAnswer[]>(worker, {
    type: "GET_USER_ANSWERS",
    data: { cityEscapeId, enigmaId },
    opts: {
      traceKey: `EnigmaUserAnswersWorker-${Date.now()}`,
      response: {
        transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
      },
    },
  }).finally(() => {
    worker.terminate();
  });
}
